/**
 * Build a Google Maps URL that opens the place's full info panel
 * (name, photos, reviews, hours) when possible — not just a lat/lng pin.
 *
 * Resolution order:
 *   1. `query_place_id` — Google resolves the place by its stable
 *      Places ID and opens its panel directly. The `query` parameter
 *      is still required by the Maps URL spec and is used as the
 *      free-text fallback when the ID can't be resolved.
 *   2. Lat/lng `query` — drops the user at the coordinates.
 *   3. Free-text `query` — falls through to the address (more
 *      disambiguating) or the name when no address is available.
 *
 * The free-text `query` value prefers the address over the name even
 * when `query_place_id` is set, since Google falls back to it when
 * the place ID has been retired.
 *
 * ADR-0044: prefer `catalogId` (stripping the `google:` prefix) over
 * the legacy `placeId` field. The `query_place_id` parameter is
 * Google-specific, so we only use it for `google:`-source entries.
 *
 * Duck-typed to avoid a cross-lib dep on `@my-fullstack-app/itinerary-types`
 * (see CLAUDE.md). `PlaceRef.id` carries the catalog id, so PlaceRef
 * callers pass `{ catalogId: ref.id, ... }`.
 *
 * Google Maps URL Action API:
 *   https://developers.google.com/maps/documentation/urls/get-started
 */
export interface PlaceMapInput {
  name: string;
  address?: string | undefined;
  /** Legacy raw Google Places ID. ADR-0044. */
  placeId?: string | undefined;
  /** Vendor-neutral catalog id ('<source>:<native>'). */
  catalogId?: string | undefined;
  lat?: number | undefined;
  lng?: number | undefined;
}

export function placeMapUrl(place: PlaceMapInput): string {
  const base = 'https://www.google.com/maps/search/?api=1';
  const hasAddress = !!place.address && place.address.trim().length > 0;
  const queryText = encodeURIComponent(hasAddress ? place.address! : place.name);
  const googlePlaceId = place.catalogId?.startsWith('google:')
    ? place.catalogId.slice('google:'.length)
    : place.placeId || undefined;
  if (googlePlaceId) {
    return `${base}&query=${queryText}&query_place_id=${encodeURIComponent(googlePlaceId)}`;
  }
  if (place.lat != null && place.lng != null) {
    return `${base}&query=${place.lat},${place.lng}`;
  }
  return `${base}&query=${queryText}`;
}
